import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Upload, message, Space, Switch, Row, Col, Divider, Typography } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';
import settingsService from '../../services/settingsService';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { VISIT_FIELDS, VISIT_FIELD_KEYS, isVisitFieldVisible } from '../../utils/visitFields';
import LabTestLibraryPicker from '../../components/LabTestLibraryPicker';

const { Paragraph } = Typography;

const DoctorSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [signatureUrl, setSignatureUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [watermarkUrl, setWatermarkUrl] = useState('');
    const { user, setUser } = useAuth();
    const { t } = useTranslation();

    useEffect(() => {
        fetchSettings();
        setLogoUrl(user?.doctorLogo || '');
        setWatermarkUrl(user?.printWatermark || '');
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await settingsService.getSettings(user._id);
            form.setFieldsValue({
                doctorName: data.doctorName,
                clinicAddress: data.clinicAddress,
                clinicPhone: data.clinicPhone,
                appointmentPayAmount: data.appointmentPayAmount || 0,
                // Anything not explicitly switched off stays on, so settings saved
                // before this feature existed keep the full form.
                visitFields: VISIT_FIELD_KEYS.reduce((acc, key) => ({
                    ...acc,
                    [key]: isVisitFieldVisible(data.visitFields, key)
                }), {}),
                // Left undefined when the doctor has never chosen: the picker reads
                // that as "everything is offered" rather than "nothing is", and an
                // untouched setting is not sent back on save.
                enabledLabTests: Array.isArray(data.enabledLabTests) ? data.enabledLabTests : undefined
            });
            setSignatureUrl(data.doctorSignature);
        } catch (error) {
            message.error('Failed to fetch settings');
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            await settingsService.updateSettings(user._id, values);
            message.success('Settings updated successfully');
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to update settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSignatureUpload = async (file) => {
        try {
            const response = await settingsService.uploadSignature(user._id, file);
            setSignatureUrl(response.fileUrl);
            message.success('Signature uploaded successfully');
        } catch (error) {
            message.error('Failed to upload signature');
        }
        return false;
    };

    // The image printed faintly behind every prescription. Kept on the user, like
    // the logo, so the print picks it up without another request.
    const handleWatermarkUpload = async (file) => {
        try {
            const response = await settingsService.uploadWatermark(user._id, file);
            setWatermarkUrl(response.fileUrl);
            setUser({ ...user, printWatermark: response.fileUrl });
            message.success(t('success'));
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
        return false;
    };

    const handleWatermarkRemove = async () => {
        try {
            await settingsService.removeWatermark(user._id);
            setWatermarkUrl('');
            const { printWatermark, ...rest } = user;
            setUser(rest);
            message.success(t('success'));
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleLogoUpload = async (file) => {
        try {
            const response = await settingsService.uploadLogo(user._id, file);
            setLogoUrl(response.fileUrl);
            // Keep the logged-in user in sync so prescription headers use it immediately.
            setUser({ ...user, doctorLogo: response.fileUrl });
            message.success('Logo uploaded successfully');
        } catch (error) {
            message.error('Failed to upload logo');
        }
        return false;
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={
                    <h2 style={{
                        margin: 0,
                        background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '24px',
                        fontWeight: 700
                    }}>
                        Clinic Settings
                    </h2>
                }
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(2, 128, 144, 0.08)'
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Doctor Name"
                        name="doctorName"
                        rules={[{ required: true, message: 'Please enter doctor name' }]}
                    >
                        <Input size="large" placeholder="Enter doctor name" />
                    </Form.Item>

                    <Form.Item
                        label="Clinic Address"
                        name="clinicAddress"
                        rules={[{ required: true, message: 'Please enter clinic address' }]}
                    >
                        <Input.TextArea
                            size="large"
                            rows={3}
                            placeholder="Enter clinic address"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Clinic Phone"
                        name="clinicPhone"
                        rules={[{ required: true, message: 'Please enter clinic phone' }]}
                    >
                        <Input size="large" placeholder="Enter clinic phone number" />
                    </Form.Item>

                    <Form.Item
                        label="Appointment Payment Amount"
                        name="appointmentPayAmount"
                        rules={[{ required: true, message: 'Please enter appointment payment amount' }]}
                        extra="Default payment amount for each appointment (can be customized per patient)"
                    >
                        <Input
                            type="number"
                            size="large"
                            placeholder="Enter default appointment payment amount"
                            min={0}
                            addonAfter="IQD"
                        />
                    </Form.Item>

                    <Divider orientation="left">
                        {t('visitFormFields') || 'Visit form fields'}
                    </Divider>
                    <Paragraph type="secondary" style={{ marginTop: -8 }}>
                        {t('visitFormFieldsHint') ||
                            'Switch off anything you never fill in — it is removed from the create-visit form. Values already saved on past visits are kept and still shown in the history.'}
                    </Paragraph>

                    <Row gutter={[16, 8]} style={{ marginBottom: 24 }}>
                        {VISIT_FIELDS.map((field) => (
                            <Col xs={24} sm={12} key={field.key}>
                                <Form.Item
                                    name={['visitFields', field.key]}
                                    label={t(field.labelKey) || field.fallback}
                                    valuePropName="checked"
                                    style={{ marginBottom: 8 }}
                                >
                                    <Switch
                                        checkedChildren={t('shown') || 'Shown'}
                                        unCheckedChildren={t('hidden') || 'Hidden'}
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>

                    <Divider orientation="left">
                        {t('labTestsOffered') || 'Lab tests I order'}
                    </Divider>
                    <Paragraph type="secondary" style={{ marginTop: -8 }}>
                        {t('labTestsOfferedHint') ||
                            'Switch on the investigations your speciality actually requests. Only these appear when you order a lab test or add one to a visit. Everything is on until you change it, and this affects nobody but you.'}
                    </Paragraph>

                    <Form.Item name="enabledLabTests" style={{ marginBottom: 24 }}>
                        <LabTestLibraryPicker />
                    </Form.Item>

                    <Form.Item
                        label={t('clinicLogo') || 'Clinic / Doctor Logo'}
                        extra="Shown on the prescription header (and the branch pharmacy copy)"
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Upload
                                beforeUpload={handleLogoUpload}
                                maxCount={1}
                                accept="image/*"
                                showUploadList={false}
                            >
                                <Button icon={<UploadOutlined />}>{t('uploadLogo') || 'Upload Logo'}</Button>
                            </Upload>
                            {logoUrl && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '16px',
                                    background: '#f5f7fa',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <img
                                        src={logoUrl}
                                        alt="Logo"
                                        style={{
                                            maxWidth: '200px',
                                            maxHeight: '100px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Space>
                    </Form.Item>

                    <Form.Item
                        label={t('printWatermark') || 'Prescription watermark'}
                        extra={t('printWatermarkHint') || 'Printed faintly behind the prescription instead of the default Rx mark. A simple emblem on a transparent background works best.'}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Space>
                                <Upload
                                    beforeUpload={handleWatermarkUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    showUploadList={false}
                                >
                                    <Button icon={<UploadOutlined />}>{t('uploadWatermark') || 'Upload watermark'}</Button>
                                </Upload>
                                {watermarkUrl && (
                                    <Button danger onClick={handleWatermarkRemove}>
                                        {t('removeWatermark') || 'Remove watermark'}
                                    </Button>
                                )}
                            </Space>
                            {watermarkUrl && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '16px',
                                    background: '#f5f7fa',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <img
                                        src={watermarkUrl}
                                        alt="Watermark"
                                        style={{ maxWidth: '200px', maxHeight: '140px', objectFit: 'contain', opacity: 0.6 }}
                                    />
                                </div>
                            )}
                        </Space>
                    </Form.Item>

                    <Form.Item
                        label="Doctor Signature"
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Upload
                                beforeUpload={handleSignatureUpload}
                                maxCount={1}
                                accept="image/*"
                            >
                                <Button icon={<UploadOutlined />}>Upload Signature</Button>
                            </Upload>
                            {signatureUrl && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '16px',
                                    background: '#f5f7fa',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <img
                                        src={signatureUrl}
                                        alt="Signature"
                                        style={{
                                            maxWidth: '200px',
                                            maxHeight: '100px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Space>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<SaveOutlined />}
                            size="large"
                            style={{
                                background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                                border: 'none',
                                fontWeight: 600
                            }}
                        >
                            Save Settings
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default DoctorSettings;
